import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Badge, Skeleton, StatusDot, Sparkline, Button, ProgressBar,
} from "@coremonit/design-system";
import {
  Container as ContainerIcon, ArrowLeft, Cpu, MemoryStick, HardDrive, Wifi,
  Eye, EyeOff, Copy, Bot, Sparkles,
} from "lucide-react";
import { useContainerDetail } from "../api/hooks";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { HealthScoreRing } from "../components/HealthScoreRing";
import { RelatedResources } from "../components/RelatedResources";
import { toast } from "sonner";

export function ContainerDetailPage() {
  const { serviceId, podId, containerName } = useParams<{ serviceId: string; podId: string; containerName: string }>();
  const decodedName = containerName ? decodeURIComponent(containerName) : undefined;
  const detail = useContainerDetail(serviceId, podId, decodedName);
  const nav = useNavigate();
  const [showSecrets, setShowSecrets] = useState(false);
  const c = detail.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Breadcrumbs items={[
        { label: "Containers", path: "/containers" },
        { label: c?.serviceName ?? "Service", path: serviceId ? `/services/${serviceId}` : undefined },
        { label: c?.podName ?? "Pod" },
        { label: decodedName ?? "Container" },
      ]} />
      <PageHeader
        icon={<ContainerIcon size={16} />}
        title={decodedName ?? "Container"}
        subtitle={c ? `${c.serviceName} · ${c.namespace} · ${c.cluster} · ${c.node}` : "Loading…"}
        badge={c && <Badge color={c.status === "running" ? "green" : c.status === "waiting" ? "amber" : "red"}>{c.status}</Badge>}
        actions={
          <>
            <Button variant="ghost" icon={<ArrowLeft size={12} />} onClick={() => nav("/containers")}>Explorer</Button>
            {serviceId && <Button variant="secondary" onClick={() => nav(`/services/${serviceId}`)}>Service</Button>}
            {serviceId && <Button variant="glow" icon={<Bot size={12} />} onClick={() => nav(`/ai?service=${serviceId}`)}>Ask AI</Button>}
          </>
        }
      />

      {detail.isLoading || !c ? (
        <Skeleton height={420} radius={16} />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "start" }}>
            <GlassCard style={{ padding: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <HealthScoreRing score={c.health.score} grade={c.health.grade} size={72} />
              <StatusDot status={c.health.status} pulse={c.health.status !== "healthy"} />
            </GlassCard>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
              <InfoCard label="Image tag" value={c.imageTag} mono />
              <InfoCard label="Restarts" value={String(c.restartCount)} warn={c.restartCount > 3} />
              <InfoCard label="OOM (24h)" value={String(c.oomEvents24h)} warn={c.oomEvents24h > 0} />
              <InfoCard label="Startup" value={`${c.startupTimeSec}s`} />
              <InfoCard label="Uptime" value={`${Math.round(c.uptimeSec / 60)} min`} />
              <InfoCard label="Ports" value={c.openPorts.join(", ")} mono />
            </div>
          </div>

          <GlassCard style={{ padding: 16 }} accent="rgba(139,92,246,0.25)">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Sparkles size={14} color={C.purple} style={{ marginTop: 2 }} />
              <div>
                <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>AI health summary</div>
                <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{c.aiSummary}</div>
              </div>
            </div>
          </GlassCard>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <GlassCard style={{ padding: 16 }}>
              <SectionTitle title="Live resources" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <LiveMetric icon={<Cpu size={11} />} label="CPU" value={`${c.cpuMillicoresLive}m`} limit={c.limits.cpu} request={c.requests.cpu} color={C.blue} spark={spark(c.cpuMillicoresLive)} />
                <LiveMetric icon={<MemoryStick size={11} />} label="Memory" value={`${c.memoryMbLive}Mi`} limit={c.limits.memory} request={c.requests.memory} color={C.purple} spark={spark(c.memoryMbLive)} />
                <LiveMetric icon={<HardDrive size={11} />} label="Disk" value={`${c.diskMbLive}Mi`} color={C.cyan} spark={spark(c.diskMbLive)} />
                <LiveMetric icon={<Wifi size={11} />} label="Network" value={`${(c.networkKbpsLive / 1000).toFixed(1)} Mbps`} color={C.green} spark={spark(c.networkKbpsLive)} />
              </div>
            </GlassCard>
            <GlassCard style={{ padding: 16 }}>
              <SectionTitle title="Resource limits & requests" />
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                <LimitRow label="CPU limit" value={c.limits.cpu} pct={Math.min(100, (c.cpuMillicoresLive / 2000) * 100)} color={C.blue} />
                <LimitRow label="Memory limit" value={c.limits.memory} pct={Math.min(100, (c.memoryMbLive / 4096) * 100)} color={C.purple} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 10, color: C.textMuted }}>
                  <span>Requests: {c.requests.cpu} / {c.requests.memory}</span>
                  <span>FDs {c.openFds} · Threads {c.threads} · GC {c.gcPct}%</span>
                </div>
              </div>
            </GlassCard>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <GlassCard style={{ padding: 16 }}>
              <SectionTitle title={`Volumes (${c.volumes.length})`} />
              {c.volumes.map((v) => (
                <div key={v.name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                  <span style={{ color: C.text }}>{v.name}</span>
                  <span style={{ color: C.textMuted, fontFamily: "ui-monospace, monospace" }}>{v.mountPath}</span>
                </div>
              ))}
            </GlassCard>
            <GlassCard style={{ padding: 16 }}>
              <SectionTitle title={`Secrets (${c.secrets.length})`} />
              {c.secrets.map((s) => (
                <div key={s.name} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.text }}>
                  {s.name} <span style={{ color: C.textMuted }}>→ {s.key}</span>
                </div>
              ))}
            </GlassCard>
          </div>

          <GlassCard style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <SectionTitle title={`Environment (${c.env.length})`} />
              <Button variant="ghost" size="xs" icon={showSecrets ? <EyeOff size={11} /> : <Eye size={11} />} onClick={() => setShowSecrets((v) => !v)}>
                {showSecrets ? "Hide secrets" : "Show secrets"}
              </Button>
            </div>
            <div style={{ maxHeight: 220, overflow: "auto" }}>
              {c.env.map((e) => (
                <div key={e.key} style={{ display: "grid", gridTemplateColumns: "200px 1fr auto", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                  <span style={{ color: C.blue, fontFamily: "ui-monospace, monospace" }}>{e.key}</span>
                  <span style={{ color: C.textSub, fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.value ?? (showSecrets ? `<${e.fromSecret}>` : "•••••••••••")}
                  </span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(e.value ?? e.fromSecret ?? ""); toast.success("Copied"); }}
                    style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer" }}
                  ><Copy size={10} /></button>
                </div>
              ))}
            </div>
          </GlassCard>

          <RelatedResources links={c.links} />
        </>
      )}
    </div>
  );
}

function spark(n: number) {
  return Array.from({ length: 12 }, () => n * (0.6 + Math.random() * 0.6));
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>;
}

function InfoCard({ label, value, mono, warn }: { label: string; value: string; mono?: boolean; warn?: boolean }) {
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: warn ? C.red : C.text, fontSize: 14, fontWeight: 700, marginTop: 4, fontFamily: mono ? "ui-monospace, monospace" : undefined }}>{value}</div>
    </GlassCard>
  );
}

function LiveMetric({ icon, label, value, limit, request, color, spark: data }: {
  icon: React.ReactNode; label: string; value: string; limit?: string; request?: string; color: string; spark: number[];
}) {
  return (
    <div style={{ padding: 10, background: C.surfaceHigh, borderRadius: 10, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{icon}{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ color, fontSize: 13, fontWeight: 700 }}>{value}</span>
        <Sparkline data={data} color={color} width={56} height={18} strokeWidth={1} />
      </div>
      {limit && <div style={{ color: C.textMuted, fontSize: 9, marginTop: 4 }}>limit {limit}{request ? ` · req ${request}` : ""}</div>}
    </div>
  );
}

function LimitRow({ label, value, pct, color }: { label: string; value: string; pct: number; color: string }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.textMuted, marginBottom: 4 }}>
        <span>{label}</span><span style={{ color: C.text }}>{value}</span>
      </div>
      <ProgressBar value={pct} color={color} />
    </div>
  );
}
